import { useState, useEffect, useRef } from "react";

// ── Offline detection ─────────────────────────────────────────────────────────
export function useOffline() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);

  return isOffline;
}

// ── iOS detection ─────────────────────────────────────────────────────────────
export function useIsIOS() {
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
  const isStandalone = window.navigator.standalone === true;
  return { isIOS, isStandaloneIOS: isStandalone };
}

// ── PWA Install prompt ────────────────────────────────────────────────────────
export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Already running as standalone PWA?
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    window.addEventListener("appinstalled", () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    });

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setIsInstalled(true);
      setIsInstallable(false);
    }
    setDeferredPrompt(null);
  };

  return { isInstallable, isInstalled, install };
}

// ── Install PWA (composite — combine install prompt + iOS + popup) ────────────
export function useInstallPWA() {
  const { isInstallable, isInstalled, install } = usePWAInstall();
  const { isIOS } = useIsIOS();
  const [showPopup, setShowPopup] = useState(false);
  const popupRef = useRef(null);

  // Button is visible when not yet installed and either iOS or native prompt is ready
  const showButton = !isInstalled && (isIOS || isInstallable);

  // Close iOS popup on outside click
  useEffect(() => {
    if (!showPopup) return;
    const handler = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setShowPopup(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [showPopup]);

  const handleClick = async () => {
    if (isIOS) { setShowPopup((v) => !v); return; }
    await install();
  };

  return { showButton, isIOS, showPopup, setShowPopup, handleClick, popupRef };
}
